import { useParlorRequestB } from "@/components/ParlorRequestExperiment";
import { useCustomVariation } from "@/hooks/useCustomVariation";
import { colors, customEvent, HLine, Spacing, VStack } from "goi_common";
import { useEffect, useLayoutEffect, useMemo } from "react";
import type { ReactElement } from "react";
import { useRouter } from "next/router";
import { ParlorDetailProps } from "@/types/parlor";
import ParlorRequestQuestionStepByStep, {
  ParlorRequestVariation,
} from "./components/ParlorRequestQuestionStepByStep";
import RenewalFooter from "@/containers/HomeContainer/components/RenewalFooter";
import { safeParlorIds } from "@/constants/parlor/excludeParlors";
import useParlorRouterChangeLoad from "./hooks/useParlorRouterChangeLoad";
import {
  costLeadFunnelExperimentKey,
  isCostLeadFunnelExperimentLive,
  markCostLeadFunnelAdEntry,
  resolveCostLeadFunnelEntry,
} from "@/constants/parlor/costLeadFunnel";
import type { CostLeadFunnelEntry } from "@/constants/parlor/costLeadFunnel";
import { ParlorCostLeadFunnel } from "./CostLeadFunnel";

// Hackle 실험 #292 (match/parlor-requests, "장례식장 견적"): step 진행 UX 3-variation
// A: 기존 (이전+다음 버튼). B: 자동진행+버튼 유지. C: 자동진행+이전/다음 제거 (마지막만 LARGE CTA).
// 종료된 실험. 코드는 추적 메트릭 보존을 위해 유지 (Hackle traffic 0).
const PARLOR_REQUEST_VARIATION_KEY = 292;

// 종료 실험 승자 확정 (StepByStep에 baked-in, A/B 분기 제거):
// - #296 비용 게이지 노출 → C(헤더 아래 sticky) 승격
// - #303 게이지 그래프형 → A(막대형) 확정
// - #305 빈소 평형 추천 → B(조문객수 First + 추천) 확정
// - #298 quote LP 인트로 → A(인트로 없이 직행) 확정 (인트로 코드 제거)

// Hackle 실험 #297 (match/parlor-requests, SafeParlor 고이장례연구소 띠지 노출):
// A: 현행(띠지 X). B: 띠지(CompanyInfoBanner) 노출. SafeParlor에서만 호출 → SafeParlor 진입자만 모수 등록.
const SAFE_PARLOR_BANNER_KEY = 297;

// 종료 실험 #315(첫문항 니즈) / #323(니즈별 뒷퍼널) / #335, #337(니즈 롤백).
// #335, #337 이 B(롤백) 승자로 종료되어 첫문항과 뒷퍼널을 코드에서 제거했다(2026-09).
// 문항은 조문객수 - 평형 - status 3스텝 고정이다.
// 실험 히스토리와 판정 근거는 wiki/features/platform-parlor-req-needs-first-question-ab.md 참고.

// Hackle 실험 #345(광고 유입) / #346(오가닉·그 외 유입): 장례식장 비용 안내 리드 퍼널.
// A: 기존 견적 폼. B: 4스텝 비용 안내 퍼널 + 번호/카카오 가입.
// 같은 처치를 두 실험으로 나눈 이유와 유입 판정은 constants/parlor/costLeadFunnel.ts 참고.
// 모수는 상호 배타다 - 유입을 먼저 확정한 뒤 해당 키 하나만 평가한다.

// #370 할당은 Layout의 ParlorRequestExperiment에서 헤더와 모달이 공유한다.
// 뒤로가기 트랩은 A/B 공통, A는 기존 창, B는 새 안내 창과 견적 헤더다.

export default function ParlorRequestContainer({ parlor }: { parlor: ParlorDetailProps }) {
  const router = useRouter();
  const rawVariation = useCustomVariation(PARLOR_REQUEST_VARIATION_KEY);
  const variation: ParlorRequestVariation | "Z" =
    rawVariation === "A" || rawVariation === "B" || rawVariation === "C" ? rawVariation : "Z";

  const backTrapB = useParlorRequestB();
  useParlorRouterChangeLoad(backTrapB);

  useLayoutEffect(() => {
    customEvent({
      newGtm: {
        path: "parlor_match_request",
        trigger_and_target: "uv_parlor_match_request",
      },
    });
  }, []);

  // 광고 파라미터는 새로고침·페이지 내 이동에서 사라진다. 첫 진입에 세션 마킹을 남겨야
  // 같은 사람이 새로고침 한 번으로 오가닉 실험 모수로 넘어가지 않는다.
  useEffect(() => {
    if (!router.isReady) return;
    markCostLeadFunnelAdEntry(router.query);
  }, [router.isReady, router.query]);

  const isSafeParlor = safeParlorIds.includes(Number(parlor.id));

  const isExperimentLive = isCostLeadFunnelExperimentLive();

  // 유입 구분. 두 실험 중 **한쪽 키만** 평가해야 모수가 상호 배타가 된다.
  const entry = useMemo<CostLeadFunnelEntry | null>(
    () => (router.isReady ? resolveCostLeadFunnelEntry(router.query) : null),
    [router.isReady, router.query],
  );
  const costFunnelKey = entry ? costLeadFunnelExperimentKey(entry) : 0;

  if (variation === "Z") return null;

  // 실험이 살아있는 동안에는 유입 확정 전에 화면을 그리지 않는다. 먼저 기존 화면을 그리면
  // A안이 잠깐 보였다 B로 바뀌고, 그 사이 SafeParlor 의 #297 모수에도 B 할당자가 섞인다.
  if (isExperimentLive && !router.isReady) return null;

  const existingRequestForm = isSafeParlor ? (
    <SafeParlor parlor={parlor} variation={variation} />
  ) : (
    <NotSafeParlor parlor={parlor} variation={variation} />
  );

  return (
    <VStack id="wrapper" width="100%" maxWidth="480px" margin={"0 auto"}>
      {costFunnelKey > 0 ? (
        <CostLeadFunnelGate
          experimentKey={costFunnelKey}
          parlor={parlor}
          fallback={existingRequestForm}
        />
      ) : (
        existingRequestForm
      )}
      <Spacing gap={120} />
      <HLine height="1px" color={colors.gray200} />
      <RenewalFooter />
    </VStack>
  );
}

/**
 * 비용 안내 리드 퍼널 실험 게이트.
 *
 * 유입이 확정되고 해당 키가 발급된 뒤에만 마운트된다(#296 `ParlorPriceGaugeProbe` 패턴).
 * 컨테이너에서 훅을 바로 부르면 유입 미확정 구간에서 잘못된 키가 평가돼 모수가 섞인다.
 *
 * `fallback` 은 엘리먼트로 받는다. B 에서는 렌더되지 않아 기존 폼 쪽 훅(#297 등)이
 * 호출되지 않는다 - 즉 B 할당자는 기존 폼의 실험 모수에 등록되지 않는다.
 */
const CostLeadFunnelGate = ({
  experimentKey,
  parlor,
  fallback,
}: {
  experimentKey: number;
  parlor: ParlorDetailProps;
  fallback: ReactElement;
}) => {
  const variation = useCustomVariation(experimentKey);

  // B 만 새 퍼널, 나머지(A / Z=미해결)는 기존 화면. Z 에 null 을 주면 번호는 발급했지만
  // Hackle 에서 실험을 아직 running 으로 켜지 않은 구간에 **전원 빈 화면**이 된다.
  // 위 #292 게이트가 이미 SDK 준비를 보장하므로 A->B 플리커 구간도 없다.
  if (variation === "B") return <ParlorCostLeadFunnel parlor={parlor} />;
  return fallback;
};

const SafeParlor = ({
  parlor,
  variation,
}: {
  parlor: ParlorDetailProps;
  variation: ParlorRequestVariation;
}) => {
  // #297 useCustomVariation은 여기서 호출 → SafeParlor 진입자만 모수 등록.
  const rawSafeBannerVariation = useCustomVariation(SAFE_PARLOR_BANNER_KEY);
  const showSafeBanner = rawSafeBannerVariation === "B";

  return (
    <ParlorRequestQuestionStepByStep
      parlor={parlor}
      isVariationB
      variation={variation}
      showSafeBanner={showSafeBanner}
    />
  );
};

const NotSafeParlor = ({
  parlor,
  variation,
}: {
  parlor: ParlorDetailProps;
  variation: ParlorRequestVariation;
}) => {
  return (
    <ParlorRequestQuestionStepByStep parlor={parlor} isVariationB={true} variation={variation} />
  );
};
